import React from 'react';
import Container from 'common/src/components/UI/Container';
import { useTranslation } from 'react-i18next';
import qrAndroidApk from './qr-android-apk.svg';
import qrAppStore from './qr-app-store.svg';
import qrGooglePlay from './qr-google-play.svg';

import {
  AsideList,
  AsideListItem,
  AsideText,
  AsideTitle,
  BrowserActions,
  BrowserAside,
  BrowserBadge,
  BrowserDescription,
  BrowserLayout,
  BrowserList,
  BrowserMeta,
  BrowserRow,
  BrowserTitle,
  CardFooter,
  CardGrid,
  CardLabel,
  CardText,
  CardTitle,
  DownloadCard,
  Eyebrow,
  FooterCallout,
  FooterCalloutActions,
  FooterCalloutText,
  FooterCalloutTitle,
  HeroActions,
  HeroGrid,
  HeroHighlights,
  HeroSection,
  HeroText,
  HeroTitle,
  HighlightCard,
  HighlightLabel,
  HighlightText,
  HighlightTitle,
  InlineAction,
  InlineGhostAction,
  MobileActions,
  MobileCardTop,
  PageShell,
  PrimaryAction,
  QRFrame,
  QRHint,
  QRPanel,
  SecondaryAction,
  Section,
  SectionIntro,
  SectionLabel,
  SectionText,
  SectionTitle,
  Snippet,
  SpotlightActions,
  SpotlightCard,
  StickyNav,
  StickyNavInner,
  StickyNavLink,
  StoreBadgeButton,
  StoreBadgeEyebrow,
  StoreBadgeIcon,
  StoreBadgeLabel,
  StoreBadgeText,
  TertiaryNote,
} from './downloadShowcase.style';

const AppleStoreIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M16.72 12.64c.03 3.18 2.79 4.24 2.82 4.25-.02.07-.44 1.51-1.45 2.99-.87 1.28-1.77 2.56-3.2 2.59-1.4.03-1.85-.83-3.45-.83-1.6 0-2.1.8-3.42.86-1.38.05-2.43-1.38-3.31-2.66-1.8-2.61-3.17-7.39-1.33-10.57.91-1.58 2.55-2.58 4.33-2.61 1.35-.03 2.62.91 3.45.91.83 0 2.39-1.13 4.03-.97.69.03 2.63.28 3.88 2.11-.1.06-2.32 1.35-2.35 3.93Zm-2.15-6.45c.73-.89 1.22-2.13 1.08-3.36-1.05.04-2.31.7-3.06 1.59-.68.79-1.28 2.06-1.12 3.27 1.16.09 2.37-.59 3.1-1.5Z"
    />
  </svg>
);

const GooglePlayIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="#00d17a"
      d="M3.37 2.7 13.9 13.23l-10.5 8.99a2 2 0 0 1-.4-1.2V3.9c0-.43.13-.84.37-1.2Z"
    />
    <path
      fill="#00a6ff"
      d="m16.62 10.5-2.72 2.72L3.37 2.7c.1-.15.24-.28.39-.39l12.86 8.2Z"
    />
    <path
      fill="#ff4f81"
      d="m16.62 13.95-12.86 8.2a2 2 0 0 1-.39-.39l10.53-8.53 2.72.72Z"
    />
    <path
      fill="#ffb300"
      d="M21.35 11.33c.86.55.86 1.8 0 2.35l-4.73 2.97-2.72-3.43 2.72-2.72 4.73 2.4Z"
    />
  </svg>
);

const StoreBadge = ({ href, badgeType, children }) => {
  const badgeContent =
    badgeType === 'play' ? (
      <>
        <StoreBadgeIcon>
          <GooglePlayIcon />
        </StoreBadgeIcon>
        <StoreBadgeText>
          <StoreBadgeEyebrow>GET IT ON</StoreBadgeEyebrow>
          <StoreBadgeLabel>Google Play</StoreBadgeLabel>
        </StoreBadgeText>
      </>
    ) : (
      <>
        <StoreBadgeIcon>
          <AppleStoreIcon />
        </StoreBadgeIcon>
        <StoreBadgeText>
          <StoreBadgeEyebrow>Download on the</StoreBadgeEyebrow>
          <StoreBadgeLabel>App Store</StoreBadgeLabel>
        </StoreBadgeText>
      </>
    );

  return (
    <StoreBadgeButton
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={children}
    >
      {badgeContent}
    </StoreBadgeButton>
  );
};

const DownloadShowcase = ({ pageContext }) => {
  const { t } = useTranslation();

  const withLang = (path) => {
    if (!path || path.startsWith('http') || path.startsWith('#')) {
      return path;
    }

    return `${pageContext.langPathPrefix}${path}`;
  };

  const sectionLinks = [
    { id: 'browser-extensions', label: t('Browser Extension') },
    { id: 'desktop-apps', label: t('Desktop') },
    { id: 'web-app', label: t('Web') },
    { id: 'mobile-apps', label: t('Mobile') },
    { id: 'command-line', label: t('Command Line') },
  ];

  const browserItems = [
    {
      name: 'Chrome',
      badge: 'CH',
      store: t('Chrome Web Store'),
      description: t(
        'Install the official extension directly from the Chrome Web Store.'
      ),
      primary: {
        label: t('Install extension'),
        url: 'https://chrome.google.com/webstore/detail/psonopw/eljmjmgjkbmpmfljlmklcfineebidmlo',
      },
      secondary: {
        label: t('Learn more'),
        url: '/google-chrome-password-manager',
      },
    },
    {
      name: 'Firefox',
      badge: 'FF',
      store: t('Firefox Add-ons'),
      description: t(
        'Use the native Firefox add-on with full Psono browser integration.'
      ),
      primary: {
        label: t('Install extension'),
        url: 'https://addons.mozilla.org/firefox/addon/psono-pw-password-manager/',
      },
      secondary: {
        label: t('Learn more'),
        url: '/mozilla-firefox-password-manager',
      },
    },
    {
      name: 'Edge',
      badge: 'ED',
      store: t('Microsoft Edge Add-ons'),
      description: t(
        'Use the dedicated Edge listing if you prefer Microsofts extension store.'
      ),
      primary: {
        label: t('Install extension'),
        url: 'https://microsoftedge.microsoft.com/addons/detail/psono-free-password-man/abobmepfpbkapdlmfhnnkebcnhgeccbm',
      },
      secondary: {
        label: t('Learn more'),
        url: '/microsoft-edge-password-manager',
      },
    },
    {
      name: 'Brave',
      badge: 'BR',
      store: t('Chrome Web Store'),
      description: t(
        'Brave users can install the Chrome extension directly from the Chrome Web Store.'
      ),
      primary: {
        label: t('Open store'),
        url: 'https://chrome.google.com/webstore/detail/psonopw/eljmjmgjkbmpmfljlmklcfineebidmlo',
      },
    },
    {
      name: 'Opera',
      badge: 'OP',
      store: t('Chrome Web Store'),
      description: t(
        'Opera can use the same Chromium extension listing for a fast setup.'
      ),
      primary: {
        label: t('Open store'),
        url: 'https://chrome.google.com/webstore/detail/psonopw/eljmjmgjkbmpmfljlmklcfineebidmlo',
      },
    },
    {
      name: 'Vivaldi',
      badge: 'VI',
      store: t('Chrome Web Store'),
      description: t(
        'Vivaldi works with the Chromium build of the Psono extension.'
      ),
      primary: {
        label: t('Open store'),
        url: 'https://chrome.google.com/webstore/detail/psonopw/eljmjmgjkbmpmfljlmklcfineebidmlo',
      },
    },
    {
      name: 'Chromium',
      badge: 'CR',
      store: t('Chrome Web Store'),
      description: t(
        'Chromium-based browsers can install the same extension from the Chrome Web Store.'
      ),
      primary: {
        label: t('Open store'),
        url: 'https://chrome.google.com/webstore/detail/psonopw/eljmjmgjkbmpmfljlmklcfineebidmlo',
      },
    },
  ];

  const desktopCards = [
    {
      label: t('Windows'),
      title: t('Windows Installer (.exe)'),
      text: t(
        'Download the native Windows installer for a direct desktop setup on Windows systems.'
      ),
      primary: {
        label: t('Download .exe'),
        url: 'https://get.psono.com/psono/psono-app/latest/psono-windows-installer.exe',
      },
    },
    {
      label: t('macOS'),
      title: t('Mac App Store'),
      text: t(
        "Get the native macOS app from Apple's App Store with regular updates."
      ),
      primary: {
        label: t('Open App Store'),
        url: 'https://apps.apple.com/us/app/psono-password-manager/id1545581224?platform=mac',
      },
    },
    {
      label: t('Linux'),
      title: t('AppImage'),
      text: t(
        'Use the portable Linux build when you want the desktop app without repository setup.'
      ),
      primary: {
        label: t('Download AppImage'),
        url: 'https://get.psono.com/psono/psono-app/latest/psono-linux-x64.AppImage',
      },
    },
    {
      label: t('Linux'),
      title: t('Debian and Ubuntu Repo'),
      text: t(
        'Install and update the desktop app through the signed APT repository on package.psono.com.'
      ),
      snippet: `curl -fsSL https://package.psono.com/keys/psono-archive-key.asc | sudo gpg --dearmor -o /usr/share/keyrings/psono-archive-keyring.gpg
echo "deb [arch=amd64 signed-by=/usr/share/keyrings/psono-archive-keyring.gpg] https://package.psono.com/linux/deb stable main" | sudo tee /etc/apt/sources.list.d/psono.list > /dev/null
sudo apt update
sudo apt install psono`,
      primary: {
        label: t('Archive key'),
        url: 'https://package.psono.com/keys/psono-archive-key.asc',
      },
      secondary: {
        label: t('APT metadata'),
        url: 'https://package.psono.com/linux/deb/dists/stable/InRelease',
      },
      note: t(
        'Signing key fingerprint: 8CE66303F9A609E0652C8B6B8B5148E325315EFF'
      ),
    },
    {
      label: t('Linux'),
      title: t('RPM Repo'),
      text: t(
        'Use the RPM repository for Fedora, RHEL-compatible systems and other rpm-based desktops.'
      ),
      snippet: `sudo rpm --import https://package.psono.com/keys/psono-archive-key.asc
printf '%s\n' '[psono]' 'name=Psono' 'baseurl=https://package.psono.com/linux/rpm/stable/x86_64' 'enabled=1' 'gpgcheck=1' 'repo_gpgcheck=1' 'gpgkey=https://package.psono.com/keys/psono-archive-key.asc' | sudo tee /etc/yum.repos.d/psono.repo > /dev/null
sudo dnf install psono`,
      primary: {
        label: t('Archive key'),
        url: 'https://package.psono.com/keys/psono-archive-key.asc',
      },
      secondary: {
        label: t('RPM metadata'),
        url: 'https://package.psono.com/linux/rpm/stable/x86_64/repodata/repomd.xml',
      },
      note: t(
        'Signing key fingerprint: 8CE66303F9A609E0652C8B6B8B5148E325315EFF'
      ),
    },
  ];

  const mobileCards = [
    {
      label: t('Android'),
      title: t('Google Play'),
      text: t(
        'Install the Android app from Google Play for autofill, vault access and passkey support.'
      ),
      badgeType: 'play',
      qrCode: qrGooglePlay,
      qrAlt: t('Google Play QR code'),
      primary: {
        label: t('Open Play Store'),
        url: 'https://play.google.com/store/apps/details?id=com.psono.psono',
      },
      secondary: {
        label: t('Learn more'),
        url: '/google-android-password-manager',
      },
    },
    {
      label: t('iPhone and iPad'),
      title: t('App Store'),
      text: t(
        "Get the native iOS app from Apple's App Store for secure vault access on the go."
      ),
      badgeType: 'appstore',
      qrCode: qrAppStore,
      qrAlt: t('App Store QR code'),
      primary: {
        label: t('Open App Store'),
        url: 'https://apps.apple.com/us/app/psono-password-manager/id1545581224',
      },
      secondary: {
        label: t('Learn more'),
        url: '/apple-ios-password-manager',
      },
    },
    {
      label: t('Direct download'),
      title: t('Android APK'),
      text: t(
        'Prefer a direct package? Download the latest Android APK directly from the release channel.'
      ),
      badgeType: 'apk',
      qrCode: qrAndroidApk,
      qrAlt: t('Android APK QR code'),
      primary: {
        label: t('Download APK'),
        url: 'https://get.psono.com/psono/psono-app/latest/app.apk',
      },
      secondary: {
        label: t('Desktop and mobile source'),
        url: 'https://gitlab.com/esaqa/psono/psono-app',
      },
    },
  ];

  const cliCards = [
    {
      label: t('Linux x86_64'),
      title: 'psono-ci',
      text: t(
        'Download the x86_64 binary for CI pipelines, automation jobs and secure secret retrieval.'
      ),
      primary: {
        label: t('Download binary'),
        url: 'https://get.psono.com/psono/psono-ci/x86_64-linux/psonoci',
      },
      secondary: {
        label: t('Install guide'),
        url: 'https://doc.psono.com/user/psonoci/install.html',
      },
    },
    {
      label: t('Linux ARMv7'),
      title: 'psono-ci',
      text: t(
        'Run the command line utility on ARMv7 devices and lightweight build agents.'
      ),
      primary: {
        label: t('Download binary'),
        url: 'https://get.psono.com/psono/psono-ci/armv7-linux/psonoci',
      },
      secondary: {
        label: t('Install guide'),
        url: 'https://doc.psono.com/user/psonoci/install.html',
      },
    },
    {
      label: t('Linux AArch64'),
      title: 'psono-ci',
      text: t(
        'Use the AArch64 build for modern ARM servers, cloud runners and edge workloads.'
      ),
      primary: {
        label: t('Download binary'),
        url: 'https://get.psono.com/psono/psono-ci/aarch64-linux/psonoci',
      },
      secondary: {
        label: t('Install guide'),
        url: 'https://doc.psono.com/user/psonoci/install.html',
      },
    },
  ];

  return (
    <PageShell>
      <HeroSection>
        <Container>
          <HeroGrid>
            <div>
              <Eyebrow>{t('Download Psono')}</Eyebrow>
              <HeroTitle>
                {t('Pick the right Psono app for every device and workflow.')}
              </HeroTitle>
              <HeroText>
                {t(
                  'Install the browser extension, desktop app, mobile app, web client or command line utility from one focused download hub.'
                )}
              </HeroText>
              <HeroActions>
                <PrimaryAction href="#browser-extensions">
                  {t('Explore downloads')}
                </PrimaryAction>
                <SecondaryAction
                  href="https://www.psono.pw/"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('Open web app')}
                </SecondaryAction>
              </HeroActions>
            </div>
            <HeroHighlights>
              {sectionLinks.map((item) => (
                <HighlightCard href={`#${item.id}`} key={item.id}>
                  <HighlightLabel>{t('Available now')}</HighlightLabel>
                  <HighlightTitle>{item.label}</HighlightTitle>
                  <HighlightText>
                    {item.id === 'browser-extensions' &&
                      t('Chrome, Firefox, Edge and the wider Chromium family.')}
                    {item.id === 'desktop-apps' &&
                      t(
                        'APT and RPM repositories plus portable desktop builds.'
                      )}
                    {item.id === 'web-app' &&
                      t(
                        'Instant access in the hosted community edition at psono.pw.'
                      )}
                    {item.id === 'mobile-apps' &&
                      t(
                        'Native apps for Android and iOS, with a direct APK option.'
                      )}
                    {item.id === 'command-line' &&
                      t(
                        'psono-ci binaries for secure automation in pipelines and scripts.'
                      )}
                  </HighlightText>
                </HighlightCard>
              ))}
            </HeroHighlights>
          </HeroGrid>
        </Container>
      </HeroSection>

      <StickyNav>
        <Container>
          <StickyNavInner>
            {sectionLinks.map((item) => (
              <StickyNavLink href={`#${item.id}`} key={item.id}>
                {item.label}
              </StickyNavLink>
            ))}
          </StickyNavInner>
        </Container>
      </StickyNav>

      <Section id="browser-extensions">
        <Container>
          <SectionIntro>
            <SectionLabel>{t('Browser Extension')}</SectionLabel>
            <SectionTitle>
              {t('Use Psono directly where logins happen.')}
            </SectionTitle>
            <SectionText>
              {t(
                'Choose your browser and install the Psono extension from the official store. Safari is not supported at the moment.'
              )}
            </SectionText>
          </SectionIntro>

          <BrowserLayout>
            <BrowserAside>
              <AsideTitle>{t('One extension, many browsers.')}</AsideTitle>
              <AsideText>
                {t(
                  'Psono covers the major browsers people actually use in daily work, with official store listings for Chrome, Firefox and Edge plus support for Chromium-based browsers.'
                )}
              </AsideText>
              <AsideList>
                <AsideListItem>
                  {t('Official extension stores for Chrome, Firefox and Edge.')}
                </AsideListItem>
                <AsideListItem>
                  {t('Chromium-based browsers can use the Chrome store build.')}
                </AsideListItem>
                <AsideListItem>
                  {t(
                    'Passkeys, autofill and secure vault access in the browser.'
                  )}
                </AsideListItem>
              </AsideList>
            </BrowserAside>

            <BrowserList>
              {browserItems.map((item) => (
                <BrowserRow key={item.name}>
                  <BrowserBadge>{item.badge}</BrowserBadge>
                  <div>
                    <BrowserTitle>{item.name}</BrowserTitle>
                    <BrowserMeta>{item.store}</BrowserMeta>
                    <BrowserDescription>{item.description}</BrowserDescription>
                  </div>
                  <BrowserActions>
                    <InlineAction
                      href={withLang(item.primary.url)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {item.primary.label}
                    </InlineAction>
                    {item.secondary && (
                      <InlineGhostAction href={withLang(item.secondary.url)}>
                        {item.secondary.label}
                      </InlineGhostAction>
                    )}
                  </BrowserActions>
                </BrowserRow>
              ))}
            </BrowserList>
          </BrowserLayout>
        </Container>
      </Section>

      <Section id="desktop-apps">
        <Container>
          <SectionIntro>
            <SectionLabel>{t('Desktop')}</SectionLabel>
            <SectionTitle>
              {t('Native desktop access for focused everyday work.')}
            </SectionTitle>
            <SectionText>
              {t(
                'The desktop app is built in psono-app and is available as direct downloads for Windows and macOS, plus signed Linux repositories and portable desktop builds.'
              )}
            </SectionText>
          </SectionIntro>

          <CardGrid>
            {desktopCards.map((item) => (
              <DownloadCard key={item.title}>
                <CardLabel>{item.label}</CardLabel>
                <CardTitle>{item.title}</CardTitle>
                <CardText>{item.text}</CardText>
                {item.snippet && <Snippet>{item.snippet}</Snippet>}
                {item.note && <TertiaryNote>{item.note}</TertiaryNote>}
                <CardFooter>
                  {item.primary && (
                    <InlineAction
                      href={withLang(item.primary.url)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {item.primary.label}
                    </InlineAction>
                  )}
                  {item.secondary && (
                    <InlineGhostAction
                      href={withLang(item.secondary.url)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {item.secondary.label}
                    </InlineGhostAction>
                  )}
                </CardFooter>
              </DownloadCard>
            ))}
          </CardGrid>
        </Container>
      </Section>

      <Section id="web-app">
        <Container>
          <SectionIntro>
            <SectionLabel>{t('Web App')}</SectionLabel>
            <SectionTitle>
              {t('Need instant access? Open Psono in the browser.')}
            </SectionTitle>
            <SectionText>
              {t(
                'The hosted community edition gives you direct access without a local install and is perfect when you want to start using Psono immediately.'
              )}
            </SectionText>
          </SectionIntro>

          <SpotlightCard>
            <CardLabel>{t('Hosted community edition')}</CardLabel>
            <CardTitle>psono.pw</CardTitle>
            <CardText>
              {t(
                'Launch the web app from any modern browser, sign in and keep working with the same secure Psono experience.'
              )}
            </CardText>
            <SpotlightActions>
              <InlineAction
                href="https://www.psono.pw/"
                target="_blank"
                rel="noreferrer"
              >
                {t('Open psono.pw')}
              </InlineAction>
              <InlineGhostAction
                href="https://psono.com/security"
                target="_blank"
                rel="noreferrer"
              >
                {t('Security overview')}
              </InlineGhostAction>
            </SpotlightActions>
          </SpotlightCard>
        </Container>
      </Section>

      <Section id="mobile-apps">
        <Container>
          <SectionIntro>
            <SectionLabel>{t('Mobile')}</SectionLabel>
            <SectionTitle>{t('Take your vault with you.')}</SectionTitle>
            <SectionText>
              {t(
                'Install the native mobile apps on Android and iOS, or use the direct APK when that fits your deployment model better.'
              )}
            </SectionText>
          </SectionIntro>

          <CardGrid>
            {mobileCards.map((item) => (
              <DownloadCard key={item.title}>
                <CardLabel>{item.label}</CardLabel>
                <MobileCardTop>
                  <div>
                    <CardTitle>{item.title}</CardTitle>
                    <CardText>{item.text}</CardText>
                  </div>
                  <QRPanel>
                    <QRFrame>
                      <img
                        src={item.qrCode}
                        alt={item.qrAlt}
                        width="124"
                        height="124"
                      />
                    </QRFrame>
                    <QRHint>{t('Scan with your phone')}</QRHint>
                  </QRPanel>
                </MobileCardTop>
                <MobileActions>
                  {item.badgeType === 'play' ||
                  item.badgeType === 'appstore' ? (
                    <>
                      <StoreBadge
                        href={withLang(item.primary.url)}
                        badgeType={item.badgeType}
                      >
                        {item.primary.label}
                      </StoreBadge>
                    </>
                  ) : (
                    <InlineAction
                      href={withLang(item.primary.url)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {item.primary.label}
                    </InlineAction>
                  )}
                  <InlineGhostAction
                    href={withLang(item.secondary.url)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item.secondary.label}
                  </InlineGhostAction>
                </MobileActions>
              </DownloadCard>
            ))}
          </CardGrid>
        </Container>
      </Section>

      <Section id="command-line">
        <Container>
          <SectionIntro>
            <SectionLabel>{t('Command Line')}</SectionLabel>
            <SectionTitle>{t('Automate secrets with psono-ci.')}</SectionTitle>
            <SectionText>
              {t(
                'Download the CLI utility for the architecture your runners use and plug Psono straight into your CI and deployment workflows.'
              )}
            </SectionText>
          </SectionIntro>

          <CardGrid>
            {cliCards.map((item) => (
              <DownloadCard key={`${item.title}-${item.label}`}>
                <CardLabel>{item.label}</CardLabel>
                <CardTitle>{item.title}</CardTitle>
                <CardText>{item.text}</CardText>
                <CardFooter>
                  <InlineAction
                    href={withLang(item.primary.url)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item.primary.label}
                  </InlineAction>
                  <InlineGhostAction
                    href={withLang(item.secondary.url)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item.secondary.label}
                  </InlineGhostAction>
                </CardFooter>
              </DownloadCard>
            ))}
          </CardGrid>

          <FooterCallout>
            <FooterCalloutTitle>
              {t('Need your own Psono stack as well?')}
            </FooterCalloutTitle>
            <FooterCalloutText>
              {t(
                'Psono can also be self-hosted with dedicated installation guides for Community Edition and Enterprise Edition deployments.'
              )}
            </FooterCalloutText>
            <FooterCalloutActions>
              <PrimaryAction
                href="https://doc.psono.com/admin/installation/install-psono-ce.html"
                target="_blank"
                rel="noreferrer"
              >
                {t('Install Community Edition')}
              </PrimaryAction>
              <SecondaryAction
                href="https://doc.psono.com/admin/installation/install-psono-ee.html"
                target="_blank"
                rel="noreferrer"
              >
                {t('Install Enterprise Edition')}
              </SecondaryAction>
            </FooterCalloutActions>
          </FooterCallout>
        </Container>
      </Section>
    </PageShell>
  );
};

export default DownloadShowcase;
